import React, { useEffect, useRef, useState } from "react";

/* ----------------------------------------------------
UTILITY FUNCTIONS
---------------------------------------------------- */

function normalize(t = "") {
  return String(t)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenize(t = "") {
  const stop = new Set([
    "a", "an", "and", "are", "as", "at", "be", "but", "by", "for", "from",
    "had", "has", "have", "he", "in", "is", "it", "its", "of", "on", "or",
    "that", "the", "their", "there", "they", "this", "to", "was", "were",
    "what", "when", "where", "which", "who", "why", "with", "would",
    "about", "into", "than", "then", "them", "those", "these", "tell",
    "me", "did", "does", "how", "many", "within", "show", "source",
    "sources", "paper", "papers", "research", "mention", "mentions",
    "find", "list", "question", "ask", "explain"
  ]);

  return normalize(t)
    .split(" ")
    .filter((w) => w.length > 2 && !stop.has(w));
}

function count(text, token) {
  if (!text || !token) return 0;
  const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const r = new RegExp(`\\b${escaped}\\b`, "g");
  const m = text.match(r);
  return m ? m.length : 0;
}

function displayDocName(name = "") {
  return String(name || "Document")
    .replace(/\.txt$/i, "")
    .replace(/\.pdf$/i, "")
    .replace(/_/g, " ")
    .trim();
}

function uniqueBy(items, keyFn) {
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const key = keyFn(item);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}

function pickTopicWord(query = "") {
  const weak = new Set([
    "roman",
    "romans",
    "britain",
    "britannia",
    "british",
    "change",
    "changed",
    "important",
    "importance",
    "role",
    "system",
    "systems",
    "evidence",
    "subject",
    "history",
    "historian",
    "historians"
  ]);

  const strong = tokenize(query).filter((t) => !weak.has(t));
  if (strong.length) return strong[0];

  const fallback = tokenize(query);
  return fallback[0] || "this subject";
}

function toHeading(word = "") {
  const clean = String(word || "").trim();
  if (!clean) return "Answer";
  return clean.charAt(0).toUpperCase() + clean.slice(1);
}

function cleanChunkText(text = "") {
  return String(text)
    .replace(/Location\s+Evidence\s+Type\s+Description\s+Source/gi, "")
    .replace(/\b(Location|Evidence|Type|Description|Source)\b.*?(?=\.)/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

/* ----------------------------------------------------
RANKING
---------------------------------------------------- */

function scoreChunk(chunk, tokens) {
  const text = normalize(chunk.text || "");
  const doc = normalize(chunk.doc || "");
  const file = normalize(chunk.file || "");
  let s = 0;

  for (const t of tokens) {
    s += count(doc, t) * 20;
    s += count(file, t) * 14;
    s += count(text, t) * 4;
  }

  return s;
}

function rankDocuments(chunks, query) {
  const tokens = tokenize(query);
  const byDoc = new Map();

  for (const c of chunks) {
    const sc = scoreChunk(c, tokens);
    if (sc <= 0) continue;

    const key = c.doc || c.file || "Research Document";

    if (!byDoc.has(key)) {
      byDoc.set(key, { doc: key, score: 0, chunks: [] });
    }

    const e = byDoc.get(key);
    e.score += sc;
    e.chunks.push({ ...c, score: sc });
  }

  return [...byDoc.values()].sort((a, b) => b.score - a.score);
}

/* ----------------------------------------------------
INTENT DETECTION
---------------------------------------------------- */

function isDiscoveryQuery(q) {
  const n = normalize(q);
  return (
    /\b(show|list|find)\b/.test(n) ||
    /\bpaper|papers|sources|documents\b/.test(n)
  );
}

function isSynthesisQuestion(q) {
  const n = normalize(q);
  return /^(how|why|what|was|were|did|could|would)/.test(n);
}

function isCompareQuestion(q) {
  const n = normalize(q);
  return (
    /\bcompare\b/.test(n) ||
    /\bversus\b/.test(n) ||
    /\bvs\b/.test(n) ||
    /\bdifference between\b/.test(n)
  );
}

function isDefinitionStyleQuery(q) {
  const n = normalize(q);
  return (
    tokenize(q).length <= 5 ||
    /^what is\b/.test(n) ||
    /^what are\b/.test(n) ||
    /^define\b/.test(n) ||
    /^tell me about\b/.test(n) ||
    /^explain\b/.test(n)
  );
}

/* ----------------------------------------------------
TEXT EXTRACTION
---------------------------------------------------- */

function sentences(text = "") {
  return cleanChunkText(text)
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .filter(Boolean);
}

function isBadSentence(s = "") {
  const badPattern =
    /abstract|appendix|appendices|chapter|references|bibliography|keywords|gazetteer|introduction/i;

  if (badPattern.test(s)) return true;
  if (/^\d+/.test(s.trim())) return true;
  if (s.length > 250) return true;
  if (/^location evidence type description source/i.test(s.trim())) return true;
  return false;
}
function bestSentences(chunks, query, limit = 4) {
  const tokens = tokenize(query);
  const results = [];

  for (const c of chunks) {
    const text = cleanChunkText(c.text || "");

    for (const s of sentences(text)) {
      const trimmed = s.trim();

      if (isBadSentence(trimmed)) continue;
if (/^\s*".+"\.\s*[A-Z]/.test(s)) return true;

      // ❌ still reject obvious table rows
      if (
        /linguistic|evidence type|description source/i.test(trimmed) ||
        /\b[A-Z][a-z]+ \([^)]+\)\s+Linguistic/i.test(trimmed)
      ) {
        continue;
      }

      // ✅ allow shorter definition-style lines
      if (trimmed.length < 25) continue;

      const n = normalize(trimmed);
      let sc = 0;

      for (const t of tokens) {
        if (n.includes(t)) sc += 3;
      }

      // boost definition-like lines
      if (/was|were|is|are|refers to/i.test(trimmed)) {
        sc += 2;
      }

      if (sc > 0) {
        results.push({ s: trimmed, sc });
      }
    }
  }

  return uniqueBy(
    results.sort((a, b) => b.sc - a.sc),
    (item) => normalize(item.s)
  )
    .slice(0, limit)
    .map((o) => o.s);
}


/* ----------------------------------------------------
SOURCE BUILDERS
---------------------------------------------------- */

function buildUniqueSources(chunks = []) {
  const seen = new Set();
  const out = [];

  for (const c of chunks) {
    const doc = c.doc || "Research Document";
    const pdf = c.file
      ? String(c.file).replace(/\.txt$/i, ".pdf")
      : `${doc}.pdf`;

    const key = `${doc}|${pdf}`;
    if (seen.has(key)) continue;
    seen.add(key);

    out.push({
      doc,
      url: `/images/research/pdfs/${encodeURIComponent(pdf)}`
    });
  }

  return out;
}

/* ----------------------------------------------------
ANSWER BUILDERS
---------------------------------------------------- */

function historianSummary(chunks, query, doc) {
  const s = bestSentences(chunks, query, 4);

  if (!s.length) {
    return "Relevant material was found, but a clear answer could not be formed.";
  }

  const topic = pickTopicWord(query);
  const heading = toHeading(topic);

  const main = s[0];
  const supporting = s.slice(1, 3).join(" ");

  let out = `${heading}\n\n${main}`;

  if (supporting) {
    out += ` ${supporting}`;
  }

  if (out.length > 520) {
    out = out.slice(0, 520).trim();
    if (!out.endsWith(".") && !out.endsWith("...")) out += "...";
  }

  return out;
}

function historianSynthesis(ranked, query) {
  const docs = ranked.slice(0, 3);
  const chunks = docs.flatMap((d) =>
    d.chunks
      .sort((a, b) => b.score - a.score)
      .slice(0, 2)
  );

  const s = bestSentences(chunks, query, 6);
  const topic = pickTopicWord(query);

  if (s.length === 0) {
    return "Relevant research exists but a synthesis summary could not be generated.";
  }

  const intro =
    `Historians studying Roman Britain interpret ${topic} through several strands of archaeological and historical evidence preserved in the Roman Storyworld archive.`;

  const body = s.slice(0, 3).join(" ");
  const context = s.slice(3).join(" ");

  let out = `${intro}

${body}

Taken together these sources show how ${topic} formed part of the wider military, social and landscape systems of Roman Britain.

${context}`.trim();

  if (out.length > 800) {
    out = out.slice(0, 800).trim();
    if (!out.endsWith(".") && !out.endsWith("...")) out += "...";
  }

  return out;
}

function extractCompareTerms(q) {
  const n = normalize(q);

  if (n.includes("compare")) {
    const after = n.split("compare")[1];
    const parts = after
      .split(/\band\b|\bvs\b|\bversus\b/)
      .map((x) => x.trim())
      .filter(Boolean);

    if (parts.length >= 2) return [parts[0], parts[1]];
  }

  if (n.includes("difference between")) {
    const after = n.split("difference between")[1];
    const parts = after
      .split(/\band\b/)
      .map((x) => x.trim())
      .filter(Boolean);

    if (parts.length >= 2) return [parts[0], parts[1]];
  }

  if (n.includes(" vs ")) {
    const parts = n.split(/\bvs\b/).map((x) => x.trim()).filter(Boolean);
    if (parts.length >= 2) return [parts[0], parts[1]];
  }

  if (n.includes(" versus ")) {
    const parts = n.split(/\bversus\b/).map((x) => x.trim()).filter(Boolean);
    if (parts.length >= 2) return [parts[0], parts[1]];
  }

  return [];
}

function compareModeAnswer(chunks, query) {
  const [left, right] = extractCompareTerms(query);

  if (!left || !right) {
    return {
      content: "I could not determine the two subjects to compare.",
      sources: []
    };
  }

  const leftRank = rankDocuments(chunks, left);
  const rightRank = rankDocuments(chunks, right);

  const leftChunks =
    leftRank[0]?.chunks.sort((a, b) => b.score - a.score).slice(0, 2) || [];
  const rightChunks =
    rightRank[0]?.chunks.sort((a, b) => b.score - a.score).slice(0, 2) || [];

  const leftS = bestSentences(leftChunks, left, 2);
  const rightS = bestSentences(rightChunks, right, 2);

  let content =
    `Within the Roman Storyworld archive, ${displayDocName(left)} and ${displayDocName(right)} appear in different but sometimes overlapping contexts. `;

  if (leftS.length) {
    content += `${displayDocName(left)}: ${leftS.join(" ")} `;
  }

  if (rightS.length) {
    content += `${displayDocName(right)}: ${rightS.join(" ")} `;
  }

  content +=
    "Taken together the archive suggests both similarities and contrasts between these subjects in Roman Britain.";

  return {
    content,
    sources: buildUniqueSources([...leftChunks, ...rightChunks])
  };
}

function buildDiscoveryAnswerAndSources(rankedDocs, query) {
  const sources = rankedDocs
    .slice(0, 6)
    .map((docEntry) => {
      const firstChunk = docEntry.chunks[0] || {};
      const pdf = firstChunk.file
        ? String(firstChunk.file).replace(/\.txt$/i, ".pdf")
        : `${docEntry.doc}.pdf`;

      return {
        doc: docEntry.doc,
        url: `/images/research/pdfs/${encodeURIComponent(pdf)}`,
        score: docEntry.score
      };
    });

  const topic = pickTopicWord(query);

  const content =
    sources.length === 1
      ? `I found one main research document in the archive that matches ${topic}.`
      : `I found ${sources.length} research documents in the archive that match ${topic}.`;

  return { content, sources };
}

/* ----------------------------------------------------
CHAT COMPONENT
---------------------------------------------------- */

export default function FloatingChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [lastUserMessage, setLastUserMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  async function send(query) {
    setLoading(true);

    try {
      const res = await fetch("/images/research/research_chunks.json");
      const chunks = await res.json();

      const ranked = rankDocuments(chunks, query);

      if (!ranked.length) {
        setMessages((p) => [
          ...p,
          { role: "assistant", content: "No relevant research found.", sources: [] }
        ]);
        return;
      }

      if (isCompareQuestion(query)) {
        const r = compareModeAnswer(chunks, query);
        setMessages((p) => [...p, { role: "assistant", ...r }]);
        return;
      }

      if (isDiscoveryQuery(query)) {
        const r = buildDiscoveryAnswerAndSources(ranked, query);
        setMessages((p) => [...p, { role: "assistant", ...r }]);
        return;
      }

      if (isDefinitionStyleQuery(query)) {
        const best = ranked[0];
        const top = best.chunks
          .sort((a, b) => b.score - a.score)
          .slice(0, 4);

        const answer = historianSummary(top, query, best.doc);
        const sources = buildUniqueSources(top);

        setMessages((p) => [
          ...p,
          { role: "assistant", content: answer, sources }
        ]);
        return;
      }

      if (isSynthesisQuestion(query) && tokenize(query).length > 5) {
        const answer = historianSynthesis(ranked, query);
        const sources = buildUniqueSources(
          ranked.slice(0, 3).flatMap((d) => d.chunks.slice(0, 1))
        );

        setMessages((p) => [
          ...p,
          { role: "assistant", content: answer, sources }
        ]);
        return;
      }

      const best = ranked[0];
      const top = best.chunks
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);

      const answer = historianSummary(top, query, best.doc);
      const sources = buildUniqueSources(top);

      setMessages((p) => [
        ...p,
        { role: "assistant", content: answer, sources }
      ]);
    } finally {
      setLoading(false);
    }
  }

  async function submit(e) {
    e.preventDefault();
    const q = input.trim();
    if (!q || loading) return;

    setMessages((p) => [...p, { role: "user", content: q }]);
    setLastUserMessage(q);
    setInput("");
    await send(q);
  }

  async function regenerate() {
    if (!lastUserMessage || loading) return;
    await send(lastUserMessage);
  }

  function clearChat() {
    setMessages([]);
  }

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 rounded-full bg-emerald-600 text-white w-12 h-12 shadow-lg"
      >
        {open ? "×" : "💬"}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 w-[360px] bg-stone-900 text-stone-100 rounded-xl border border-stone-800 flex flex-col">
          <div className="flex justify-between items-start px-4 py-3 border-b border-stone-800">
            <div>
              <div className="font-semibold">
                Explore the Research Archive
              </div>

              <div className="text-[11px] text-stone-400 mt-1">
                Ask about Roman Britain, tribes, roads, archaeology, or compare two subjects.
              </div>
            </div>

            <div className="flex gap-2">
              <button
                onClick={clearChat}
                className="text-xs px-2 py-1 rounded bg-stone-800 hover:bg-stone-700"
              >
                Clear
              </button>

              <button
                onClick={() => setOpen(false)}
                className="text-sm px-2 py-1 rounded bg-stone-800 hover:bg-stone-700"
              >
                ✕
              </button>
            </div>
          </div>

          <div
            ref={scrollRef}
            className="px-3 pt-3 pb-2 h-[380px] overflow-y-auto space-y-3"
          >
            {messages.length === 0 && (
              <div className="rounded-lg bg-stone-800 px-3 py-2 text-sm">
                Hello. Ask me about Roman Storyworld research and I will answer from the local research archive.
              </div>
            )}

            {messages.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : ""}`}
              >
                <div
                  className={`rounded-lg px-3 py-2 text-sm ${
                    m.role === "user" ? "bg-emerald-700" : "bg-stone-800"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{m.content}</div>

                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-3 text-[11px]">
                      <div className="font-medium mb-1">
                        {m.sources.length > 1 ? "Sources" : "Source"}
                      </div>

                      {m.sources.map((s, i) => (
                        <a
                          key={i}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full rounded bg-emerald-600 px-3 py-2 text-xs font-medium text-left mt-1"
                        >
                          {displayDocName(s.doc)}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {loading && (
              <div className="rounded-lg bg-stone-800 px-3 py-2 text-sm">
                Searching the archive...
              </div>
            )}
          </div>

          <form onSubmit={submit} className="border-t border-stone-800 p-3 flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question…"
              className="flex-1 rounded bg-stone-800 px-3 py-2 text-sm"
            />

            <button
              type="submit"
              className="rounded bg-emerald-600 px-3 py-2 text-sm"
            >
              Send
            </button>

            <button
              type="button"
              onClick={regenerate}
              className="rounded bg-stone-800 px-3 py-2 text-sm"
            >
              Regenerate
            </button>
          </form>
        </div>
      )}
    </>
  );
}